import React, { useState } from 'react';

import { ActionIcon, Button, Flex, ScrollArea, Slider, Stack, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { EvaluationModalView } from 'pages/ManageSmartAssistant/Sections/EvaluationsSection/EvaluationModal/CreateEvaluationModal';
import { useTranslation } from 'react-i18next';
import { EvaluationService } from 'services/evaluation-service';
import { NotificationService } from 'services/notification-service';
import {
  INITIAL_TESTS_COUNT,
  MAX_SCENARIOS,
  MAX_SCENARIO_LENGTH,
  MAX_TESTS_COUNT,
  MIN_SCENARIOS,
  MIN_TESTS_COUNT
} from 'shared/constants/evaluation';
import { EvalsGenerateSingleStepQuestionsDto } from 'shared/interfaces/Evaluation';
import {
  maxLengthValidator,
  maxNumberValidator,
  minNumberValidator,
  requiredInputValidator
} from 'shared/utils/form-validators';

interface EvaluationModalGenerateViewProps {
  smartAssistantId: string;
  onGenerated: (questions: string[]) => void;
  setModalView: (view: EvaluationModalView) => void;
}

export function EvaluationModalGenerateView({
  smartAssistantId,
  onGenerated,
  setModalView
}: EvaluationModalGenerateViewProps) {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);

  const form = useForm({
    initialValues: {
      scenarios: [{ scenario: '' }],
      testCount: INITIAL_TESTS_COUNT
    },
    validate: {
      scenarios: {
        scenario: (value) => requiredInputValidator(value) || maxLengthValidator(value, MAX_SCENARIO_LENGTH)
      },
      testCount: (value) =>
        requiredInputValidator(value) ||
        minNumberValidator(value, MIN_TESTS_COUNT) ||
        maxNumberValidator(value, MAX_TESTS_COUNT)
    }
  });

  const generateMutation = useMutation({
    mutationFn: (generateQuestionsDto: EvalsGenerateSingleStepQuestionsDto) => {
      setIsGenerating(true);
      return EvaluationService.generateSingleStepQuestions(generateQuestionsDto);
    },
    onSuccess: (generateQuestionsResponseDto) => {
      onGenerated(generateQuestionsResponseDto.singleStepQuestions);
    },
    onError: () => {
      NotificationService.showResult(false, 'FEED_BACK.EVALUATION.FAILURE.GENERATE');
    },
    onSettled: () => {
      setIsGenerating(false);
    }
  });

  const addScenario = () => {
    form.setFieldValue('scenarios', [...form.values.scenarios, { scenario: '' }]);
  };

  const removeScenario = (index: number) => {
    const updatedScenarios = [...form.values.scenarios];
    updatedScenarios.splice(index, 1);
    form.setFieldValue('scenarios', updatedScenarios);
    form.setErrors({});
  };

  const handleGenerateQuestions = () => {
    const validation = form.validate();
    if (validation.hasErrors) return;

    generateMutation.mutate({
      smartAssistantId,
      scenarios: form.values.scenarios.map((s) => s.scenario),
      numberOfQuestions: form.values.testCount
    });
  };

  const handleBack = () => {
    setModalView('select');
    form.reset();
  };

  return (
    <Stack spacing="md">
      <Stack spacing={4}>
        <Title order={4}>{t('SMART_ASSISTANT.EVALUATIONS_SECTION.SCENARIOS')}</Title>
        <ScrollArea.Autosize mah={300}>
          <Stack spacing={8} pr={6}>
            {form.values.scenarios.map((_, index) => (
              <Flex key={index} gap={8} align="center">
                <TextInput
                  style={{ flex: 1 }}
                  placeholder={t('SMART_ASSISTANT.EVALUATIONS_SECTION.SCENARIO_PLACEHOLDER')}
                  {...form.getInputProps(`scenarios.${index}.scenario`)}
                />
                <ActionIcon
                  disabled={form.values.scenarios.length <= MIN_SCENARIOS}
                  onClick={() => removeScenario(index)}>
                  <IconTrash />
                </ActionIcon>
              </Flex>
            ))}
          </Stack>
        </ScrollArea.Autosize>
      </Stack>

      <Button
        disabled={form.values.scenarios.length >= MAX_SCENARIOS}
        leftIcon={<IconPlus />}
        variant="default"
        onClick={addScenario}
        style={{ alignSelf: 'flex-start' }}>
        {t('SMART_ASSISTANT.EVALUATIONS_SECTION.ADD_SCENARIO')}
      </Button>

      <Stack spacing={12}>
        <Title order={4}>{t('SMART_ASSISTANT.EVALUATIONS_SECTION.TEST_COUNT', { count: form.values.testCount })}</Title>
        <Slider min={MIN_TESTS_COUNT} max={MAX_TESTS_COUNT} step={1} {...form.getInputProps('testCount')} />
      </Stack>

      <Flex gap={16} justify="flex-end" mt={24}>
        <Button variant="outline" onClick={handleBack}>
          {t('APP.ACTIONS.BACK')}
        </Button>
        <Button onClick={handleGenerateQuestions} loading={isGenerating}>
          {t('SMART_ASSISTANT.EVALUATIONS_SECTION.GENERATE_QUESTIONS')}
        </Button>
      </Flex>
    </Stack>
  );
}
